import { Router } from "express";
import { z } from "zod";
import { and, count, eq, ne } from "drizzle-orm";
import { db } from "../../db/index";
import { users } from "../../db/schema/identity";
import {
  roles,
  permissions,
  modules,
  permissionRole,
  roleUser,
  userPermissions,
  moduleSettings,
} from "../../db/schema/rbac";
import {
  requireAuth,
  requirePermission,
  getEffectivePermissions,
  markPermissionsStale,
  syncUserPermissions,
  SCOPE,
  type ScopeId,
} from "../../rbac/index";
import { buildNavigation } from "./navigation";

/**
 * B1 RBAC API. Mirrors legacy V2 RolePermissionController (index/storeRole) + non-V2 store()
 * propagation + ModuleController (toggle), re-implemented natively (no entrust). Every route is
 * authenticated; mutations gate on the scoped permission and are company-scoped. PHP-faithful.
 */
export const rbacRouter = Router();
rbacRouter.use(requireAuth);

const MANAGE_RP = "manage_role_permission_setting";
const MANAGE_MODULE = "manage_module_setting";

/** Current user's effective scoped permissions (the client uses this to gate nav/UI). */
rbacRouter.get("/me/permissions", async (req, res) => {
  res.json({ permissions: await getEffectivePermissions(req.auth!.userId) });
});

/** Roles + the scoped ability matrix for the selected role (admin role = implicit ALL). */
rbacRouter.get("/role-permissions", requirePermission(MANAGE_RP), async (req, res) => {
  const companyId = req.auth!.companyId;
  const roleRows = await db
    .select({ id: roles.id, name: roles.name, displayName: roles.displayName })
    .from(roles)
    .where(eq(roles.companyId, companyId))
    .orderBy(roles.id);

  const counts = await db
    .select({ roleId: roleUser.roleId, c: count() })
    .from(roleUser)
    .groupBy(roleUser.roleId);
  const countByRole = new Map(counts.map((r) => [r.roleId, Number(r.c)]));
  const roleList = roleRows.map((r) => ({ ...r, userCount: countByRole.get(r.id) ?? 0 }));

  const reqRole = Number(req.query.role ?? 0);
  const selected =
    roleList.find((r) => r.id === reqRole) ??
    roleList.find((r) => r.name !== "admin") ??
    roleList[0] ??
    null;
  const isAdmin = selected?.name === "admin";

  let matrix: unknown[] = [];
  let totalAbilities = 0;
  if (selected && !isAdmin) {
    const mods = await db
      .select({ id: modules.id, name: modules.moduleName })
      .from(modules)
      .where(ne(modules.moduleName, "messages"))
      .orderBy(modules.id);
    const perms = await db
      .select({
        id: permissions.id,
        name: permissions.name,
        displayName: permissions.displayName,
        moduleId: permissions.moduleId,
        allowed: permissions.allowedPermissions,
      })
      .from(permissions);
    const current = await db
      .select({ pid: permissionRole.permissionId, scope: permissionRole.permissionTypeId })
      .from(permissionRole)
      .where(eq(permissionRole.roleId, selected.id));
    const scopeByPerm = new Map(current.map((c) => [c.pid, c.scope as ScopeId]));
    const roleType = ["employee", "client"].includes(selected.name) ? selected.name : "employee";
    const enabledMods = new Set(
      (
        await db
          .select({ m: moduleSettings.moduleName })
          .from(moduleSettings)
          .where(
            and(
              eq(moduleSettings.companyId, companyId),
              eq(moduleSettings.status, "active"),
              eq(moduleSettings.type, roleType as "employee" | "client"),
            ),
          )
      ).map((r) => r.m).concat(["settings", "dashboards"]),
    );

    const permsByModule = new Map<number, typeof perms>();
    for (const p of perms) {
      const arr = permsByModule.get(p.moduleId) ?? [];
      arr.push(p);
      permsByModule.set(p.moduleId, arr);
    }
    matrix = mods
      .map((m) => {
        const abilities = (permsByModule.get(m.id) ?? [])
          .map((p) => {
            const allowed = p.allowed ? (JSON.parse(p.allowed) as Record<string, number>) : {};
            if (!Object.keys(allowed).length) return null;
            return {
              id: p.id,
              name: p.name,
              label: p.displayName || p.name.replace(/_/g, " "),
              allowed, // label → type_id
              current: scopeByPerm.get(p.id) ?? SCOPE.NONE,
            };
          })
          .filter(Boolean);
        totalAbilities += abilities.length;
        return abilities.length
          ? { module: m.name, enabled: enabledMods.has(m.name), abilities }
          : null;
      })
      .filter(Boolean);
  }

  res.json({ roles: roleList, selected, isAdmin, totalAbilities, matrix });
});

/** Set a role's scope for one permission, and propagate to its non-customised users (legacy store()). */
const ScopeSet = z.object({
  roleId: z.coerce.number().int().positive(),
  permissionId: z.coerce.number().int().positive(),
  scope: z.coerce.number().int().min(1).max(5),
});
rbacRouter.post("/role-permissions/scope", requirePermission(MANAGE_RP), async (req, res) => {
  const parsed = ScopeSet.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: z.prettifyError(parsed.error) });
  const { roleId, permissionId, scope } = parsed.data;

  const role = (
    await db
      .select({ id: roles.id, name: roles.name })
      .from(roles)
      .where(and(eq(roles.id, roleId), eq(roles.companyId, req.auth!.companyId)))
      .limit(1)
  )[0];
  if (!role) return res.status(404).json({ error: "Role not found" });
  if (role.name === "admin") return res.status(422).json({ error: "Admin role is fixed at ALL" });

  // upsert the role's scope
  const existing = await db
    .select({ pid: permissionRole.permissionId })
    .from(permissionRole)
    .where(and(eq(permissionRole.permissionId, permissionId), eq(permissionRole.roleId, roleId)))
    .limit(1);
  if (existing.length) {
    await db
      .update(permissionRole)
      .set({ permissionTypeId: scope })
      .where(and(eq(permissionRole.permissionId, permissionId), eq(permissionRole.roleId, roleId)));
  } else {
    await db
      .insert(permissionRole)
      .values({ permissionId, roleId, permissionTypeId: scope });
  }

  // propagate to this role's users who haven't customised (employee-only-role rule, like legacy)
  const roleUsers = await db
    .select({ userId: roleUser.userId, customised: users.customisedPermissions })
    .from(roleUser)
    .innerJoin(users, eq(users.id, roleUser.userId))
    .where(eq(roleUser.roleId, roleId));
  for (const ru of roleUsers) {
    const onlyThisRole = role.name !== "employee" || (await userRoleCount(ru.userId)) === 1;
    if (ru.customised || !onlyThisRole) continue;
    await db
      .insert(userPermissions)
      .values({ userId: ru.userId, permissionId, permissionTypeId: scope })
      .onDuplicateKeyUpdate({ set: { permissionTypeId: scope } });
    await markPermissionsStale(ru.userId); // also invalidates cache
  }

  res.json({ ok: true });
});

async function userRoleCount(userId: number): Promise<number> {
  const r = await db.select({ c: count() }).from(roleUser).where(eq(roleUser.userId, userId));
  return Number(r[0]?.c ?? 0);
}

/** Native role create (legacy V2 storeRole — company_id explicit, name globally unique, seed scopes). */
const CreateRole = z.object({
  name: z.string().trim().min(1).max(191),
  importFromRoleId: z.coerce.number().int().positive().optional(),
});
rbacRouter.post("/role-permissions/role", requirePermission(MANAGE_RP), async (req, res) => {
  const parsed = CreateRole.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: z.prettifyError(parsed.error) });
  const slug = parsed.data.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  if (!slug) return res.status(422).json({ error: "Enter a valid role name" });
  if ((await db.select({ id: roles.id }).from(roles).where(eq(roles.name, slug)).limit(1)).length)
    return res.status(422).json({ error: "A role with that name already exists" });

  const id = await db.transaction(async (tx) => {
    await tx
      .insert(roles)
      .values({ name: slug, displayName: parsed.data.name, companyId: req.auth!.companyId });
    const roleId = (
      await tx.select({ id: roles.id }).from(roles).where(eq(roles.name, slug)).limit(1)
    )[0]!.id;

    let rows: { roleId: number; permissionId: number; permissionTypeId: number }[];
    const importId = parsed.data.importFromRoleId;
    if (importId) {
      const src = await tx
        .select({ pid: permissionRole.permissionId, scope: permissionRole.permissionTypeId })
        .from(permissionRole)
        .where(eq(permissionRole.roleId, importId));
      rows = src.map((s) => ({ roleId, permissionId: s.pid, permissionTypeId: s.scope }));
    } else {
      const allPerms = await tx.select({ id: permissions.id }).from(permissions);
      rows = allPerms.map((p) => ({ roleId, permissionId: p.id, permissionTypeId: SCOPE.NONE }));
    }
    for (let i = 0; i < rows.length; i += 200) await tx.insert(permissionRole).values(rows.slice(i, i + 200));
    return roleId;
  });

  res.json({ ok: true, id, message: "Role created" });
});

/** List toggleable modules + their on/off status for the company (legacy ModuleController.index). */
const TOGGLEABLE = [
  "payroll", "recruit", "performance", "asset", "lifecycle", "letter",
  "knowledgebase", "events", "contracts", "products", "bankaccount",
] as const;
rbacRouter.get("/custom-modules", requirePermission(MANAGE_MODULE), async (req, res) => {
  const rows = await db
    .select({ m: moduleSettings.moduleName, status: moduleSettings.status })
    .from(moduleSettings)
    .where(and(eq(moduleSettings.companyId, req.auth!.companyId), eq(moduleSettings.type, "admin")));
  const statusByMod = new Map(rows.map((r) => [r.m, r.status]));
  res.json({
    modules: TOGGLEABLE.map((name) => ({
      name,
      label: name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      enabled: (statusByMod.get(name) ?? "deactive") === "active",
    })),
  });
});

/** Toggle a module on/off for the company (legacy ModuleController.toggle). */
const ToggleModule = z.object({
  moduleName: z.string().min(1),
  type: z.enum(["admin", "employee", "client"]).default("admin"),
  status: z.enum(["active", "deactive"]),
});
rbacRouter.post("/custom-modules/toggle", requirePermission(MANAGE_MODULE), async (req, res) => {
  const parsed = ToggleModule.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: z.prettifyError(parsed.error) });
  const { moduleName, type, status } = parsed.data;
  const companyId = req.auth!.companyId;

  const existing = await db
    .select({ id: moduleSettings.id })
    .from(moduleSettings)
    .where(
      and(
        eq(moduleSettings.companyId, companyId),
        eq(moduleSettings.moduleName, moduleName),
        eq(moduleSettings.type, type),
      ),
    )
    .limit(1);
  if (existing.length) {
    await db.update(moduleSettings).set({ status }).where(eq(moduleSettings.id, existing[0]!.id));
  } else {
    await db.insert(moduleSettings).values({ companyId, moduleName, type, status });
  }
  res.json({ ok: true });
});

/** Per-user override: set one permission's scope for a user + flag them customised (legacy update()). */
const Override = z.object({
  permissionId: z.coerce.number().int().positive(),
  scope: z.coerce.number().int().min(1).max(5),
});
rbacRouter.post("/user-permissions/:userId/override", requirePermission(MANAGE_RP), async (req, res) => {
  const uid = Number(req.params.userId);
  const tgt = (
    await db
      .select({ id: users.id })
      .from(users)
      .where(and(eq(users.id, uid), eq(users.companyId, req.auth!.companyId)))
      .limit(1)
  )[0];
  if (!tgt) return res.status(404).json({ error: "User not found" });
  const parsed = Override.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: z.prettifyError(parsed.error) });

  await db
    .insert(userPermissions)
    .values({ userId: uid, permissionId: parsed.data.permissionId, permissionTypeId: parsed.data.scope })
    .onDuplicateKeyUpdate({ set: { permissionTypeId: parsed.data.scope } });
  await db.update(users).set({ customisedPermissions: true }).where(eq(users.id, uid));
  await markPermissionsStale(uid); // invalidates cache; next read preserves the override (customised)
  res.json({ ok: true });
});

/** Reset a user's overrides → re-materialize from their primary role, clear customised (legacy resetPermissions()). */
rbacRouter.post("/user-permissions/:userId/reset", requirePermission(MANAGE_RP), async (req, res) => {
  const uid = Number(req.params.userId);
  const tgt = (
    await db
      .select({ id: users.id })
      .from(users)
      .where(and(eq(users.id, uid), eq(users.companyId, req.auth!.companyId)))
      .limit(1)
  )[0];
  if (!tgt) return res.status(404).json({ error: "User not found" });
  await db.update(users).set({ customisedPermissions: false }).where(eq(users.id, uid));
  await syncUserPermissions(uid); // rebuild from roles (now non-customised) + invalidate
  res.json({ ok: true });
});

/** The current user's allowed navigation tree (perms + enabled modules). */
rbacRouter.get("/me/navigation", async (req, res) => {
  // Admin-only "Viewing as" preview: ?as=<roleName> renders the nav as that role would see it.
  const userId = req.auth!.userId;
  const companyId = req.auth!.companyId;
  const as = typeof req.query.as === "string" ? req.query.as : undefined;
  let asRole: string | undefined;
  if (as) {
    const adminRole = await db
      .select({ id: roleUser.roleId })
      .from(roleUser)
      .innerJoin(roles, eq(roles.id, roleUser.roleId))
      .where(and(eq(roleUser.userId, userId), eq(roles.name, "admin")))
      .limit(1);
    if (adminRole[0]) asRole = as; // only admins may preview another role
  }
  res.json({ navigation: await buildNavigation(userId, companyId, asRole) });
});

/** Current user identity for the topbar profile menu (real name/email/roles; admin gates view-as). */
rbacRouter.get("/me", async (req, res) => {
  const userId = req.auth!.userId;
  const [u] = await db
    .select({ id: users.id, name: users.name, email: users.email, image: users.image })
    .from(users)
    .where(eq(users.id, userId))
    .limit(1);
  const roleRows = await db
    .select({ name: roles.name, displayName: roles.displayName })
    .from(roleUser)
    .innerJoin(roles, eq(roles.id, roleUser.roleId))
    .where(eq(roleUser.userId, userId));
  const roleNames = roleRows.map((r) => r.name);
  const isAdmin = roleNames.includes("admin");
  // The roles an admin may preview ("Viewing as"). Non-admins get an empty list (feature hidden).
  const companyRoles = isAdmin
    ? (
        await db
          .select({ name: roles.name, displayName: roles.displayName })
          .from(roles)
          .where(eq(roles.companyId, req.auth!.companyId))
          .orderBy(roles.id)
      ).map((r) => ({ name: r.name, label: r.displayName || r.name }))
    : [];
  res.json({ user: u ?? null, roles: roleNames, isAdmin, companyId: req.auth!.companyId, companyRoles });
});
